import { useState, useCallback } from 'react';
import {
  createPaymentRequest,
  getPaymentStatusRequest,
  getUserPaymentsRequest,
  testPaymentConnectionRequest,
  createTestPaymentRequest,
  getPaymentStatisticsRequest,
  getPaymentStatisticsByPeriodRequest,
  getPaymentsTransactionsRequest,
  getPaymentRequest,
  getDebtorsRequest,
} from '@/api';
// В usePayments.ts замінити імпорт типів:
import type {
  CreatePaymentRequest,
  PaymentResponse,
  PaymentStatisticsData,
  PeriodStatisticsData,
  PaymentTransaction,
  PaymentTransactionDetails,
  Debtor,
  PaymentStatusResponse,
} from '@/types/payments';
// Видалити: PaymentStatus,

interface UsePaymentsReturn {
  // State
  loading: boolean;
  error: string | null;

  // Actions
  createPayment: (
    data: CreatePaymentRequest,
  ) => Promise<PaymentResponse | null>;
  getPaymentStatus: (paymentId: string) => Promise<PaymentStatusResponse | null>;
  getUserPayments: (
    limit?: number,
    offset?: number,
  ) => Promise<PaymentResponse[]>;
  testConnection: () => Promise<boolean>;
  createTestPayment: () => Promise<PaymentResponse | null>;

  // Statistics Actions
  getPaymentStatistics: () => Promise<PaymentStatisticsData | null>;
  getPaymentStatisticsByPeriod: (
    period: 'this-month' | 'last-month' | 'this-quarter' | 'this-year',
  ) => Promise<PeriodStatisticsData | null>;
  getDebtors: () => Promise<Debtor[] | null>;

  // Transaction Actions
  getPaymentsTransactions: (filters?: {
    period?: string;
    propertyId?: string;
    tenantId?: string;
    limit?: number;
    offset?: number;
  }) => Promise<{ payments: PaymentTransaction[]; total: number } | null>;

  getPaymentTransactionDetails: (
    paymentId: string,
  ) => Promise<PaymentTransactionDetails | null>; // Update return type

  // Utils
  clearError: () => void;
}

export const usePayments = (): UsePaymentsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const createPayment = useCallback(
    async (data: CreatePaymentRequest): Promise<PaymentResponse | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await createPaymentRequest(data);
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Payment creation failed');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Payment creation failed';
        setError(errorMessage);
        console.error('Create payment error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getPaymentStatus = useCallback(
    async (paymentId: string): Promise<PaymentStatusResponse | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPaymentStatusRequest(paymentId);
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error('Failed to get payment status');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to get payment status';
        setError(errorMessage);
        console.error('Get payment status error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getUserPayments = useCallback(
    async (
      limit: number = 20,
      offset: number = 0,
    ): Promise<PaymentResponse[]> => {
      try {
        setLoading(true);
        setError(null);
        const response = await getUserPaymentsRequest(limit, offset);
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error('Failed to get user payments');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to get user payments';
        setError(errorMessage);
        console.error('Get user payments error:', err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const testConnection = useCallback(async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      const response = await testPaymentConnectionRequest();
      return response.data.success;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Connection test failed';
      setError(errorMessage);
      console.error('Test connection error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const createTestPayment =
    useCallback(async (): Promise<PaymentResponse | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await createTestPaymentRequest();
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(
            response.data.message || 'Test payment creation failed',
          );
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Test payment creation failed';
        setError(errorMessage);
        console.error('Create test payment error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    }, []);

  const getPaymentStatistics =
    useCallback(async (): Promise<PaymentStatisticsData | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPaymentStatisticsRequest();
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error('Failed to get payment statistics');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to get payment statistics';
        setError(errorMessage);
        console.error('Get payment statistics error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    }, []);

  const getPaymentStatisticsByPeriod = useCallback(
    async (
      period: 'this-month' | 'last-month' | 'this-quarter' | 'this-year',
    ): Promise<PeriodStatisticsData | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPaymentStatisticsByPeriodRequest(period);
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error('Failed to get payment statistics by period');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to get payment statistics by period';
        setError(errorMessage);
        console.error('Get payment statistics by period error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getDebtors = useCallback(async (): Promise<Debtor[] | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDebtorsRequest();
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to get debtors list');
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to get debtors list';
      setError(errorMessage);
      console.error('Get debtors error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPaymentsTransactions = useCallback(
    async (
      filters: {
        period?: string;
        propertyId?: string;
        tenantId?: string;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<{ payments: PaymentTransaction[]; total: number } | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPaymentsTransactionsRequest(filters);
        if (response.data.success) {
          return {
            payments: response.data.data,
            total: response.data.meta.total,
          };
        } else {
          throw new Error('Failed to get payments transactions');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to get payments transactions';
        setError(errorMessage);
        console.error('Get payments transactions error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getPaymentTransactionDetails = useCallback(
    async (paymentId: string): Promise<PaymentTransactionDetails | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPaymentRequest(paymentId);
        if (response.data.success) {
          // We cast the response to the detailed type
          return response.data.data as unknown as PaymentTransactionDetails;
        } else {
          throw new Error('Failed to get payment transaction details');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to get payment transaction details';
        setError(errorMessage);
        console.error('Get payment transaction details error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    createPayment,
    getPaymentStatus,
    getUserPayments,
    testConnection,
    createTestPayment,
    getPaymentStatistics,
    getPaymentStatisticsByPeriod,
    getDebtors,
    getPaymentsTransactions,
    getPaymentTransactionDetails,
    clearError,
  };
};
